// Utility type
type ValueOf<T> = T[keyof T];

// Insurance Status V2 types
export const InsuranceStatusCta = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  CHECK_MANUALLY: 'CHECK_MANUALLY',
  CHECK_VIDEA_LOGIN: 'CHECK_VIDEA_LOGIN',
  CONFIRM_PATIENT_DETAILS: 'CONFIRM_PATIENT_DETAILS',
  NOT_ELIGIBLE: 'NOT_ELIGIBLE',
  CONFIRM_NPI_NUMBER: 'CONFIRM_NPI_NUMBER',
  NO_PAYER_LISTED: 'NO_PAYER_LISTED',
} as const;
export type InsuranceStatusCtaValue = ValueOf<typeof InsuranceStatusCta>;

export const StatusType = {
  SUCCESS: 'success',
  WARN: 'warn',
  ERROR: 'error',
  ACTION: 'action',
} as const;
export type StatusTypeValue = ValueOf<typeof StatusType>;

export const VerificationStatus = {
  VERIFIED: 'Verified',
  FAILED: 'Failed',
  PENDING: 'Pending',
  PATIENT_NOT_FOUND: 'Patient not found',
  PAYER_ERROR: 'Payer error',
  UNKNOWN: 'Unknown',
} as const;

export type VerificationStatusValue = ValueOf<typeof VerificationStatus>;

// Zuub API types
export type GetZuubInsuranceListRequest = {
  unifiedIds: string[];
};

/**
 * Lightweight data for displaying in the AutoVerify v3 list table.
 * Contains only the essential fields needed for the list view.
 */
export type ZuubProcedureFlag =
  | 'FREQUENCY_LIMITATION'
  | 'WAITING_PERIOD'
  | 'MISSING_TOOTH_CLAUSE'
  | 'ALTERNATE_BENEFIT'
  | 'AGE_RESTRICTION'
  | 'PRE_AUTH_REQUIRED'
  | 'PRE_AUTH_RECOMMENDED'
  | 'BENEFIT_PERIOD_EXPIRED'
  | 'MAXIMUM_EXHAUSTED'
  | 'NOT_COVERED';

export type ZuubEligibilityFlagDetail = {
  type: ZuubProcedureFlag;
  message: string;
};

export type ZuubEligibilityStatus =
  | 'ELIGIBLE'
  | 'INELIGIBLE'
  | 'PARTIALLY_COVERED'
  | 'UNKNOWN';

export type ZuubEligibilityProcedure = {
  code: string;
  description?: string;
  status: ZuubEligibilityStatus;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  network: string;
  location?: string | null;
  category?: string | null;
  coinsurancePct?: number;
  coinsuranceRange?: { start: number; end: number };
  deductibleApplies: boolean;
  requiresPreAuth: boolean;
  recommendPreAuth: boolean;
  frequencyLimit?: string;
  frequencyUsed?: number;
  ageLimit?: string;
  flags: ZuubProcedureFlag[];
  flagDetails: ZuubEligibilityFlagDetail[];
  warnings: string[];
};

export type ZuubPlanSummary = {
  payer: string;
  payerId?: number | string | null;
  groupId?: string | null;
  benefitPeriodStart?: string | null;
  benefitPeriodEnd?: string | null;
  annualMaxTotal?: number | null;
  annualMaxRemaining?: number | null;
  deductibleTotal?: number | null;
  deductibleRemaining?: number | null;
  verificationTime?: string | null;
  disclaimerCount: number;
};

export type ZuubCoverageScope = {
  description: string;
  notCovered: string;
};

export type ZuubEligibilityData = {
  plan: ZuubPlanSummary;
  network: string;
  availableNetworks: string[];
  patientAge?: number;
  checkedAt: string;
  procedures: ZuubEligibilityProcedure[];
  globalWarnings: string[];
  coverage: ZuubCoverageScope;
};

export type ZuubPlanListDataV3 = {
  unifiedAppointmentId: string;
  pmsPatientId: string;
  payerId: number | null;
  needsPayerPortalConnection: boolean;
  payerName: string | null;
  insuranceStatusV2: InsuranceStatusCtaValue | null;
  statusType: StatusTypeValue | null;
  patientDisplayName: string | null;
  remainingBenefits: number | null;
  remainingDeductible: number | null;
  totalDeductible: number | null;
  // Devtools fields
  verificationStatus: ValueOf<typeof VerificationStatus> | null;
  resultSource: string | null;
  source: string | null;
  mismatches: Record<string, unknown>[] | null;
  order: number;
  // Eligibility check data (optional — only present when eligibility check has run).
  // Array contains one entry per network (e.g. InNetwork, OutOfNetwork).
  eligibility?: ZuubEligibilityData[] | null;
};

/**
 * Lightweight list item for the AutoVerify v3 page.
 * Contains minimal data needed for the table display.
 */
export type ZuubPatientListItemV3 = {
  unifiedAppointmentId: string;
  pmsPatientId: string;
  primaryProviderId?: string | null;
  patientName: string;
  appointmentDate: string | null;
  lastRun: string | null;
  hasMultiplePlans: boolean;
  totalPlans: number;
  plans: ZuubPlanListDataV3[];
};

export type GetZuubInsuranceListResponseV3 = {
  patients: ZuubPatientListItemV3[];
  totalCount: number;
};

/**
 * Response for the detail endpoint when viewing a single patient's full insurance data (v2).
 *
 * @deprecated Use GetZuubInsuranceDetailResponseV4 instead. This is the legacy v2 response type.
 */
export type GetZuubInsuranceDetailResponse = {
  pmsPatientId: string;
  patientName: string;
  appointmentDate: string | null;
  lastRun: string | null;
  hasMultiplePlans: boolean;
  totalPlans: number;
  detailedData: ZuubPatientDetailedData[];
  found: boolean;
};

// Detail types (shared with legacy v2)
export type ZuubPatientDetailInfo = {
  firstName: string;
  lastName: string;
  displayName: string;
  dateOfBirth: string;
  gender: string;
  memberId: string;
  patientSince: string;
};

export type ZuubPlanInfo = {
  payerName: string;
  planType: string;
  groupNumber: string;
  groupName: string;
  networkStatus: string | null;
  status: string;
  effectiveDate: string;
  endDate: string;
};

export type ZuubBenefitSummary = {
  remainingBenefits: number;
  totalBenefits: number;
  usedBenefits: number;
  percentageUsed: number;
  availablePreventiveServices: number;
  deductible: {
    totalDeductible: number;
    remainingDeductible: number;
    metDeductible: number;
    percentageMet: number;
    status: string;
    networkStatus: string;
    coverageLevel: string;
  };
};

export type ZuubCoverageCategory = ZuubCoverage;

export type ZuubDisclaimersAndLimitations = ZuubLimitationsAndDisclaimers;

export type ZuubWaitingPeriodRow = ZuubWaitingPeriod;

export type ZuubTreatmentHistoryRow = ZuubTreatmentHistory;

export type ZuubTreatmentHistoryRowV2 = ZuubTreatmentHistory;

export interface ZuubClaimsAddress {
  line1: string | null;
  line2: string | null;
  addressee1: string | null;
  addressee2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
}

export type ZuubSubscriberInfo = {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
};

export type ZuubExclusionRow = {
  disclaimer: string;
  serviceTypes: string[];
};

export type ZuubOutOfPocketMaximum = {
  serviceType: string;
  individual: number | null;
  family: number | null;
  networkStatus: string | null;
  remainingAmount: number | null;
};

export type ZuubPatientDetailData = {
  unifiedAppointmentId: string;
  source: string | null;
  resultSource: string | null;
  needsPayerPortalConnection: boolean;
  payerId: number | null;
  pmsPatientId: string;
  patient: ZuubPatientDetailInfo;
  rawZuubResponse?: string | null;
  payerName?: string | null;
  insuranceStatus?: string;
  insuranceStatusV2?: InsuranceStatusCtaValue;
  statusType?: StatusTypeValue;
  verificationStatus?: VerificationStatusValue;
  plan?: ZuubPlanInfo;
  benefitSummary?: ZuubBenefitSummary;
  coverage?: ZuubCoverageCategory[];
  disclaimersAndLimitations?: ZuubDisclaimersAndLimitations;
  maximumsAndDeductibles?: ZuubMaximumsAndDeductibles;
  waitingPeriods?: ZuubWaitingPeriodRow[];
  treatmentHistory?: ZuubTreatmentHistoryRow[];
  treatmentHistoryV2?: ZuubTreatmentHistoryRowV2[];
  mismatches?: Record<string, unknown>[] | null;
  claimsAddress?: ZuubClaimsAddress;
  subscriber?: ZuubSubscriberInfo;
  exclusions?: ZuubExclusionRow[];
  outOfPocketMaximums?: ZuubOutOfPocketMaximum[];
  preApprovals?: unknown[];
  missingToothClause?: string | null;
  coordinationOfBenefits?: {
    paymentOrder: string | null;
    paymentOptionType: string | null;
  };
  orthodonticProvisions?: {
    takeOver: boolean | null;
    ageLimits: { start: number; end: number; type: string }[] | null;
    billingPeriodType: string | null;
  };
  orthoBenefitSummary?: ZuubOrthoBenefitSummary;
  lastUpdated?: string;
  createdAt?: string;
};

// --- V4 Detail Types (plan-level + per-network) ---

export type ZuubPatientDetailInfoV4 = {
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  memberId: string | null;
  patientSince: string | null;
};

export type ZuubPlanInfoV4 = {
  payerName: string | null;
  planType: 'PPO' | 'HMO' | 'Indemnity' | 'EPO' | 'POS' | null;
  groupNumber: string | null;
  groupName: string | null;
  networkStatus: string | null;
  status: string;
  effectiveDate: string | null;
  endDate: string | null;
  policyStartDate: string | null;
  policyEndDate: string | null;
  benefitPeriodType: string | null;
};

export type ZuubDeductibleInfo = {
  totalDeductible: number;
  remainingDeductible: number;
  metDeductible: number;
  percentageMet: number;
  status: string;
  networkStatus: string;
  coverageLevel: string;
};

export type ZuubBenefitSummaryV4 = {
  remainingBenefits: number;
  totalBenefits: number;
  usedBenefits: number;
  percentageUsed: number;
  availablePreventiveServices: number;
  deductible: ZuubDeductibleInfo | null;
};

export type ZuubServiceCoverage = {
  serviceName: string;
  adaCode: string | null;
  planCoverage: number;
  frequency: string | null;
  remainingBenefits: string | null;
  coInsurance: number | null;
  nextAvailable: string | null;
};

export type ZuubCoverageCategoryV4 = {
  categoryName: string;
  coInsurance: number | null;
  services: ZuubServiceCoverage[];
};

export type ZuubGeneralDisclaimerV4 = {
  text: string;
  type: string | null;
};

export type ZuubProcedureLimitation = {
  procedureCode: string;
  procedureName: string;
  limitation: string;
};

export type ZuubCategoryLimitationsV4 = {
  categoryName: string;
  limitations: ZuubProcedureLimitation[];
};

export type ZuubDisclaimersAndLimitationsV4 = {
  generalDisclaimers: ZuubGeneralDisclaimerV4[];
  categoryLimitations: ZuubCategoryLimitationsV4[];
  footerNote: string | null;
};

export type ZuubMaximumDeductibleRow = {
  serviceType: string;
  individual: number | null;
  family: number | null;
  planPeriod: string | null;
};

export type ZuubDeductibleRow = {
  serviceType: string;
  individual: number | null;
  family: number | null;
  individualRemaining: number | null;
  familyRemaining: number | null;
  planPeriod: string | null;
};

export type ZuubMaximumsAndDeductiblesV4 = {
  maximums: ZuubMaximumDeductibleRow[];
  deductibles: ZuubDeductibleRow[];
};

export type ZuubWaitingPeriodRowV4 = {
  serviceType: string;
  startDate: string | null;
  endDate: string | null;
};

export type ZuubCoverageTier = {
  tier: string;
  coInsurance: number;
};

export type ZuubNetworkDetailBlock = {
  name: string;
  status: string;
  coverage: ZuubCoverageCategoryV4[];
  coverageTiers?: ZuubCoverageTier[];
  disclaimersAndLimitations?: ZuubDisclaimersAndLimitationsV4;
  maximumsAndDeductibles?: ZuubMaximumsAndDeductiblesV4;
  waitingPeriods?: ZuubWaitingPeriodRowV4[];
  benefitSummary?: ZuubBenefitSummaryV4;
  orthoBenefitSummary?: ZuubOrthoBenefitSummary;
};

export type ZuubPatientDetailDataV4 = {
  unifiedAppointmentId: string;
  source: string | null;
  resultSource: string | null;
  needsPayerPortalConnection: boolean;
  payerId: number | null;
  /** Common payer ID from V4 endpoint (prefer over payerId for display). */
  commonPayerId?: string | null;
  pmsPatientId: string;
  order: number;
  patient: ZuubPatientDetailInfoV4;
  payerName?: string | null;
  insuranceStatus?: string;
  insuranceStatusV2?: InsuranceStatusCtaValue;
  statusType?: StatusTypeValue;
  verificationStatus?: VerificationStatusValue;
  plan?: ZuubPlanInfoV4;
  benefitSummary?: ZuubBenefitSummaryV4;
  orthoBenefitSummary?: ZuubOrthoBenefitSummary;
  treatmentHistoryV2?: ZuubTreatmentHistoryRowV2[];
  mismatches?: Record<string, unknown>[] | null;
  claimsAddress?: ZuubClaimsAddress;
  subscriber?: ZuubSubscriberInfo;
  exclusions?: ZuubExclusionRow[];
  outOfPocketMaximums?: ZuubOutOfPocketMaximum[];
  preApprovals?: unknown[];
  missingToothClause?: string | null;
  coordinationOfBenefits?: {
    paymentOrder: string | null;
    paymentOptionType: string | null;
  };
  orthodonticProvisions?: {
    takeOver: boolean | null;
    ageLimits: { start: number; end: number; type: string }[] | null;
    billingPeriodType: string | null;
  };
  lastUpdated?: string;
  createdAt?: string;
  networks: ZuubNetworkDetailBlock[];
};

/**
 * Response for the detail endpoint when viewing a single patient's full insurance data (v4).
 */
export type GetZuubInsuranceDetailResponseV4 = {
  pmsPatientId: string;
  primaryProviderId?: string | null;
  patientName: string;
  appointmentDate: string | null;
  lastRun: string | null;
  hasMultiplePlans: boolean;
  totalPlans: number;
  found: boolean;
  /** Detailed data for all insurance plans, sorted by order ascending (0 = primary, 1 = secondary, etc.) */
  detailedData: ZuubPatientDetailDataV4[];
};

/**
 * View type used by benefits page sections. Plan-level fields from V4 plus
 * network-scoped fields (coverage, benefitSummary, etc.) from the selected network.
 */
export type EffectivePlanView = Omit<ZuubPatientDetailDataV4, 'networks'> & {
  coverage?: ZuubCoverageCategoryV4[];
  coverageTiers?: ZuubCoverageTier[];
  benefitSummary?: ZuubBenefitSummaryV4;
  orthoBenefitSummary?: ZuubOrthoBenefitSummary;
  disclaimersAndLimitations?: ZuubDisclaimersAndLimitationsV4;
  maximumsAndDeductibles?: ZuubMaximumsAndDeductiblesV4;
  waitingPeriods?: ZuubWaitingPeriodRowV4[];
};

export type ZuubService = {
  serviceName: string;
  adaCode: string;
  planCoverage: number;
  frequency: string;
  remainingBenefits: string;
  coInsurance: number | null;
  nextAvailable: string | null;
};

export type ZuubCoverage = {
  categoryName: string;
  coInsurance: number;
  services: ZuubService[];
};

export type ZuubDeductibleOrMaximum = {
  serviceType: string;
  individual: number | null;
  family: number | null;
  planPeriod: string;
};

export type ZuubMaximumsAndDeductibles = {
  deductibles: ZuubDeductibleOrMaximum[];
  maximums: ZuubDeductibleOrMaximum[];
};

export type ZuubWaitingPeriod = {
  serviceType: string;
  startDate: string;
  endDate: string;
};

export type ZuubTreatmentHistoryProcedure = {
  code: string;
  name: string | null;
  category: string | null;
  location: string | null;
  surface: string | null;
};

export type ZuubTreatmentHistory = {
  date: string;
  procedures: ZuubTreatmentHistoryProcedure[];
};

export type ZuubGeneralDisclaimer = {
  text: string;
  type: string;
};

export type ZuubCategoryLimitation = {
  procedureCode: string;
  procedureName: string;
  limitation: string;
};

export type ZuubCategoryLimitations = {
  categoryName: string;
  limitations: ZuubCategoryLimitation[];
};

/** Ortho benefit summary (optional per plan on v4 detail). */
export interface ZuubOrthoBenefitSummary {
  remainingBenefits: number;
  totalBenefits: number;
  usedBenefits: number;
  percentageUsed: number;
  availablePreventiveServices: number;
  deductible: ZuubOrthoDeductibleInfo | null;
}

export interface ZuubOrthoDeductibleInfo {
  totalDeductible: number;
  remainingDeductible: number;
  metDeductible: number;
  percentageMet: number;
  status: string;
  networkStatus: string;
  coverageLevel: string;
}

export type ZuubLimitationsAndDisclaimers = {
  generalDisclaimers: ZuubGeneralDisclaimer[];
  categoryLimitations: ZuubCategoryLimitations[];
  footerNote: string;
};

export type ZuubPatientDetailedData = {
  unifiedAppointmentId: string;
  pmsPatientId: string;
  payerName?: string | null;
  insuranceStatus?: string;
  insuranceStatusV2?: InsuranceStatusCtaValue;
  statusType?: StatusTypeValue;
  verificationStatus?: string;
  resultSource?: string;
  source?: string;
  mismatches?: Record<string, unknown>;
  patient: {
    firstName: string;
    lastName: string;
    displayName: string;
    dateOfBirth: string;
    gender: string;
    memberId: string;
    patientSince: string;
  };
  plan: {
    payerName: string;
    planType: string;
    groupNumber: string;
    groupName: string;
    networkStatus: string | null;
    status: string;
    effectiveDate: string;
    endDate: string;
  };
  benefitSummary: {
    remainingBenefits: number;
    totalBenefits: number;
    usedBenefits: number;
    percentageUsed: number;
    availablePreventiveServices: number;
    deductible: {
      totalDeductible: number;
      remainingDeductible: number;
      metDeductible: number;
      percentageMet: number;
      status: string;
      networkStatus: string;
      coverageLevel: string;
    };
  };
  coverage: ZuubCoverage[];
  claimsAddress: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    country: string | null;
  };
  subscriber: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
  };
  maximumsAndDeductibles: ZuubMaximumsAndDeductibles;
  waitingPeriods: ZuubWaitingPeriod[];
  treatmentHistory: ZuubTreatmentHistory[];
  treatmentHistoryV2?: ZuubTreatmentHistory[];
  disclaimersAndLimitations: ZuubLimitationsAndDisclaimers;
  lastUpdated: string;
  createdAt: string;
  payerId: number;
  needsPayerPortalConnection: boolean;
  orthoBenefitSummary?: ZuubOrthoBenefitSummary;
};

// Zuub RCM Onboarding Types
export type ZuubPayer = {
  id: number;
  name: string;
  commonPayerId?: string | null;
  /** Portal URL from payer features. May be missing for some payers. */
  portalUrl?: string;
  /** When set, use as the "create credentials" link; prefer over portalUrl for where to send the user. */
  createCredentialUrl?: string;
  rank?: number | null;
};

export type ZuubOnboardingBeginRequest = {
  payerId: number;
  questions?: Array<{
    question: string;
    answer: string;
  }>;
  username?: string;
  password?: string;
};

export type ZuubOnboardingBeginResponse = {
  email?: string;
  phoneNumber?: string;
  username?: string;
  password?: string;
};

export type ZuubCredentialsValidationStatus =
  | 'Acknowledged'
  | 'InProgress'
  | 'Succeeded'
  | 'Failed';

export type ZuubOnboardingStatusResponse = {
  credentialStatus: ZuubCredentialsValidationStatus | null;
  credentialErrorMessage: string | null;
  onboardingStatus: string;
};

export type DependentInfo = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  memberId: string;
  sameAsSubscriber: boolean;
};

/** Request body for RCM Zuub manual benefits check (new patient). */
export type ZuubManualCheckRequest = {
  subscriberFirstName: string;
  subscriberLastName: string;
  subscriberDateOfBirth: string;
  subscriberId: string;
  groupId?: string;
  payerId: number;
  providerFirstName: string;
  providerLastName: string;
  providerNationalProviderId: string;
  dependents: DependentInfo[];
};

/** Response from RCM Zuub manual-check endpoint. */
export type ZuubManualCheckResponse = {
  requestId: string;
};

/** Response from RCM Zuub manual-check status (polling) endpoint. */
export type ZuubManualCheckStatusResponse = {
  asyncStatus: 'pending' | 'completed' | 'failed' | 'not_supported' | string | null;
  [key: string]: unknown;
};

/** Plan item in the RCM Zuub manual-check list response. */
export type ZuubManualCheckListPlan = {
  unifiedAppointmentId: string;
  requestId: string;
  submissionId?: string;
  pmsPatientId: string;
  payerId: number;
  needsPayerPortalConnection: boolean;
  payerName: string;
  insuranceStatusV2: InsuranceStatusCtaValue | null;
  statusType: string | null;
  patientDisplayName: string;
  remainingBenefits: number | null;
  remainingDeductible: number | null;
  totalDeductible: number | null;
  verificationStatus: string | null;
  resultSource: string | null;
  source: string | null;
  mismatches: unknown[] | null;
  order: number;
  eligibility: unknown[];
  syncStatus: string | null;
  asyncStatus: string | null;
};

/** Patient item in the RCM Zuub manual-check list response. */
export type ZuubManualCheckListPatient = {
  unifiedAppointmentId: string;
  pmsPatientId: string;
  primaryProviderId: string | null;
  patientName: string;
  appointmentDate: string | null;
  lastRun: string | null;
  hasMultiplePlans: boolean;
  totalPlans: number;
  plans: ZuubManualCheckListPlan[];
  dateOfBirth: string | null;
  subscriberFirstName: string | null;
  subscriberLastName: string | null;
  subscriberId: string | null;
  groupId: string | null;
  zuubPayerId: number | null;
  dependents: DependentInfo[];
  providerFirstName: string | null;
  providerLastName: string | null;
};

/** Response from RCM Zuub manual-check list endpoint. */
export type ZuubManualCheckListResponse = {
  patients: ZuubManualCheckListPatient[];
  totalCount: number;
  page: number;
  limit: number;
};

/** Patient block in RCM Zuub manual-check details response. */
export type ZuubManualCheckDetailsPatient = {
  firstName: string;
  lastName: string;
  displayName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  memberId: string | null;
  patientSince: string | null;
};

/** Plan block in RCM Zuub manual-check details response. */
export type ZuubManualCheckDetailsPlan = {
  payerName: string;
  planType: string | null;
  groupNumber: string | null;
  groupName: string | null;
  networkStatus: string | null;
  status: string | null;
  effectiveDate: string | null;
  endDate: string | null;
  policyStartDate: string | null;
  policyEndDate: string | null;
  benefitPeriodType: string | null;
};

/** Deductible in benefitSummary. */
export type ZuubManualCheckDetailsDeductible = {
  totalDeductible: number | null;
  remainingDeductible: number | null;
  metDeductible: number | null;
  percentageMet: number | null;
  status: string | null;
  networkStatus: string | null;
  coverageLevel: string | null;
};

/** Benefit summary in RCM Zuub manual-check details. */
export type ZuubManualCheckDetailsBenefitSummary = {
  remainingBenefits: number | null;
  totalBenefits: number | null;
  usedBenefits: number | null;
  percentageUsed: number | null;
  availablePreventiveServices: number | null;
  deductible: ZuubManualCheckDetailsDeductible | null;
};

/** Coverage tier in details response. */
export type ZuubManualCheckDetailsCoverageTier = {
  tier: string;
  coInsurance: number | null;
};

/** Claims address in details response. */
export type ZuubManualCheckDetailsClaimsAddress = {
  line1: string | null;
  line2: string | null;
  addressee1: string | null;
  addressee2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
};

/** Subscriber in details response. */
export type ZuubManualCheckDetailsSubscriber = {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
};

/** Single detailed item in RCM Zuub manual-check details response. */
export type ZuubManualCheckDetailsItem = {
  unifiedAppointmentId: string;
  pmsPatientId: string;
  resultSource: string | null;
  source: string | null;
  patient: ZuubManualCheckDetailsPatient;
  plan: ZuubManualCheckDetailsPlan;
  payerName: string;
  insuranceStatus: string | null;
  insuranceStatusV2: InsuranceStatusCtaValue | null;
  statusType: string | null;
  verificationStatus: string | null;
  benefitSummary: ZuubManualCheckDetailsBenefitSummary | null;
  orthoBenefitSummary?: ZuubOrthoBenefitSummary | null;
  treatmentHistoryV2: unknown[];
  claimsAddress: ZuubManualCheckDetailsClaimsAddress | null;
  subscriber: ZuubManualCheckDetailsSubscriber | null;
  payerId: number | null;
  coverageTiers: ZuubManualCheckDetailsCoverageTier[];
  missingToothClause?: string | null;
  coordinationOfBenefits?: {
    paymentOrder: string | null;
    paymentOptionType: string | null;
  } | null;
  networks?: ZuubNetworkDetailBlock[];
  [key: string]: unknown;
};

/** Response from RCM Zuub manual-check details endpoint. */
export type ZuubManualCheckDetailsResponse = {
  pmsPatientId: string;
  primaryProviderId: string | null;
  patientName: string;
  appointmentDate: string | null;
  lastRun: string | null;
  hasMultiplePlans: boolean;
  totalPlans: number;
  detailedData: ZuubManualCheckDetailsItem[];
  found: boolean;
};

/**
 * Full patient data with detailed insurance info (v4).
 * Used for the benefits page after fetching detail data.
 */
export type ZuubPatientData = {
  pmsPatientId: string;
  primaryProviderId?: string | null;
  patientName: string;
  appointmentDate: string | null;
  lastRun: string | null;
  hasMultiplePlans: boolean;
  totalPlans: number;
  detailedData: ZuubPatientDetailDataV4[];
};

// Zuub 2FA Messages Types
export interface Zuub2FAEmailMessage {
  code?: string;
  html: string;
  text: string;
  timestamp: string;
}

export type ZuubGet2FAEmailMessagesResponse = Zuub2FAEmailMessage[];

export interface Zuub2FASMSMessage {
  code?: string;
  message: string;
  timestamp: string;
}

export type ZuubGet2FASMSMessagesResponse = Zuub2FASMSMessage[];

export interface ZuubGet2FAMessagesResponse {
  emailMessages: ZuubGet2FAEmailMessagesResponse;
  smsMessages: ZuubGet2FASMSMessagesResponse;
}
